"use client";

/**
 * usePredictRealtimePrice - Real-time price streaming for Predict.fun markets
 *
 * Subscribes to `predict-market:{marketId}` channels on the shared backend WebSocket.
 * Receives `predict:price:update` events with bid/ask/midPrice data.
 * Reuses the singleton socket from usePriceStream (no duplicate connections).
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { getSharedPriceSocket, getOrCreateSharedSocket, acquireSharedSocket, releaseSharedSocket } from "./usePriceStream";

export interface PredictPriceData {
  marketId: string;
  bestBid: number | null;
  bestAsk: number | null;
  midPrice: number | null;
  timestamp: number;
}

interface UsePredictRealtimePriceOptions {
  /** Predict.fun market IDs to subscribe to */
  marketIds: string[];
  /** Whether to auto-connect (default: true) */
  enabled?: boolean;
}

interface UsePredictRealtimePriceReturn {
  /** Map of marketId -> current price data */
  prices: Map<string, PredictPriceData>;
  /** Whether the socket is connected */
  isConnected: boolean;
  /** Get price for a specific market ID */
  getPrice: (marketId: string) => PredictPriceData | undefined;
}

export function usePredictRealtimePrice(
  options: UsePredictRealtimePriceOptions
): UsePredictRealtimePriceReturn {
  const { marketIds, enabled = true } = options;
  const [prices, setPrices] = useState<Map<string, PredictPriceData>>(new Map());
  const [isConnected, setIsConnected] = useState(false);
  const subscribedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!enabled || marketIds.length === 0) return;

    let isMounted = true;
    let socket: ReturnType<typeof getSharedPriceSocket> = null;

    const handleConnect = () => setIsConnected(true);
    const handleDisconnect = () => setIsConnected(false);

    const handlePriceUpdate = (data: PredictPriceData) => {
      if (!data?.marketId) return;
      setPrices((prev) => {
        const next = new Map(prev);
        next.set(String(data.marketId), data);
        return next;
      });
    };

    const init = async () => {
      // Try existing socket first, create if needed (Predict-only pages)
      socket = getSharedPriceSocket() || await getOrCreateSharedSocket();
      if (!socket || !isMounted) return;

      acquireSharedSocket();

      socket.on("connect", handleConnect);
      socket.on("disconnect", handleDisconnect);
      if (socket.connected) setIsConnected(true);

      // Subscribe to channels
      const channels = marketIds.map((id) => `predict-market:${id}`);
      socket.emit("subscribe", channels);
      subscribedRef.current = new Set(channels);

      socket.on("predict:price:update", handlePriceUpdate);
    };

    init();

    return () => {
      isMounted = false;
      if (socket) {
        if (subscribedRef.current.size > 0) {
          socket.emit("unsubscribe", [...subscribedRef.current]);
          subscribedRef.current.clear();
        }
        socket.off("connect", handleConnect);
        socket.off("disconnect", handleDisconnect);
        socket.off("predict:price:update", handlePriceUpdate);
        releaseSharedSocket();
      }
    };
  }, [enabled, marketIds.join(",")]);

  const getPrice = useCallback(
    (marketId: string) => prices.get(String(marketId)),
    [prices]
  );

  return { prices, isConnected, getPrice };
}
